
import { useMemo } from 'react';
import { HARDCODED_RESTRICTIONS, RestrictionSetting } from '@/components/hardcodedRestrictions';

interface RestrictionHierarchy {
  parent: RestrictionSetting;
  children: RestrictionSetting[];
}

export const useRestrictionHierarchy = () => {
  const restrictions = HARDCODED_RESTRICTIONS;

  const hierarchicalRestrictions = useMemo(() => {
    // Get all parent restrictions (those without parent_id)
    const parents = restrictions.filter(r => !r.parent_id);
    
    // Get all child restrictions
    const children = restrictions.filter(r => r.parent_id);
    
    // Build hierarchy
    const hierarchy: RestrictionHierarchy[] = parents.map(parent => ({
      parent,
      children: children.filter(child => child.parent_id === parent.type)
    }));
    
    return hierarchy;
  }, [restrictions]);

  const flatRestrictions = useMemo(() => {
    return restrictions.sort((a, b) => {
      // Sort by parent first, then children under their parents
      if (!a.parent_id && !b.parent_id) {
        return a.name.localeCompare(b.name);
      }
      if (!a.parent_id && b.parent_id) return -1;
      if (a.parent_id && !b.parent_id) return 1;
      
      // Both are children, sort by parent first, then by name
      const parentA = restrictions.find(r => r.type === a.parent_id);
      const parentB = restrictions.find(r => r.type === b.parent_id);
      
      if (parentA?.name !== parentB?.name) {
        return (parentA?.name || '').localeCompare(parentB?.name || '');
      }
      
      return a.name.localeCompare(b.name);
    });
  }, [restrictions]);

  const getParentRestrictions = () => {
    return restrictions.filter(r => !r.parent_id);
  };

  const getChildrenOf = (parentType: string) => {
    return restrictions.filter(r => r.parent_id === parentType);
  };

  const isParentRestricted = (parentType: string, restrictionValues: Record<string, boolean>) => {
    return !restrictionValues[parentType];
  };

  const shouldChildBeDisabled = (child: RestrictionSetting, restrictionValues: Record<string, boolean>) => {
    if (!child.parent_id) return false;
    const parent = restrictions.find(r => r.type === child.parent_id);
    if (!parent) return false;
    return isParentRestricted(parent.type, restrictionValues);
  };

  return {
    hierarchicalRestrictions,
    flatRestrictions,
    getParentRestrictions,
    getChildrenOf,
    isParentRestricted,
    shouldChildBeDisabled,
    restrictions
  };
};
